import { useState, useMemo } from 'react';
import { ValuationMetrics, ValuationSortField, ValuationFilterType } from '@/types/valuation';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  TrendingUpIcon,
  TrendingDownIcon,
  SearchIcon,
  InfoIcon
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { formatUSD, formatRatio, formatPercent } from '@/lib/format';
import { CategoryBadge, DEFI_CATEGORIES } from '@/components/CategoryBadge';

interface ValuationTableProps {
  data: ValuationMetrics[];
  isLoading: boolean;
  onAssetSelect: (asset: ValuationMetrics) => void;
  lastUpdated?: string;
}


export function ValuationTable({ data, isLoading, onAssetSelect, lastUpdated }: ValuationTableProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<ValuationSortField>('market_cap');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [filterType, setFilterType] = useState<ValuationFilterType>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  const filteredAndSortedData = useMemo(() => {
    let filtered = data.filter(asset => {
      const matchesSearch = (asset.name?.toLowerCase() ?? '').includes(searchQuery.toLowerCase()) ||
        (asset.symbol?.toLowerCase() ?? '').includes(searchQuery.toLowerCase());

      if (!matchesSearch) return false;

      // Category filter
      if (categoryFilter !== 'all') {
        const assetCategory = (asset.defi_category || asset.category || '').toLowerCase();
        if (!assetCategory.includes(categoryFilter.toLowerCase())) {
          return false;
        }
      }

      switch (filterType) {
        case 'defi':
          return asset.data_flags.has_tvl || asset.data_flags.has_fees;
        case 'has_fees':
          return asset.data_flags.has_fees;
        case 'has_revenue':
          return asset.data_flags.has_revenue;
        case 'layer1':
          return ['ethereum', 'solana', 'avalanche', 'polygon', 'bsc'].includes((asset.defi_category || asset.category || '').toLowerCase()) ||
            ['ETH', 'SOL', 'AVAX', 'MATIC', 'BNB'].includes(asset.symbol);
        case 'layer2':
          return (asset.defi_category || asset.category || '').toLowerCase().includes('layer2') ||
            ['ARB', 'OP', 'MATIC'].includes(asset.symbol);
        default:
          return true;
      }
    });

    filtered.sort((a, b) => {
      let aValue: number | null;
      let bValue: number | null;

      if (sortField === 'deltas_tvl_7d') {
        aValue = a.deltas?.tvl_7d ?? null;
        bValue = b.deltas?.tvl_7d ?? null;
      } else {
        aValue = a[sortField] as number | null;
        bValue = b[sortField] as number | null;
      }

      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;

      const comparison = aValue > bValue ? 1 : -1;
      return sortDirection === 'desc' ? -comparison : comparison;
    });

    return filtered;
  }, [data, searchQuery, sortField, sortDirection, filterType, categoryFilter]);

  const handleSort = (field: ValuationSortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortButton = ({ field, children }: { field: ValuationSortField; children: React.ReactNode }) => (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => handleSort(field)}
      className="h-8 px-2 text-left justify-start font-medium"
    >
      {children}
      {sortField === field && (
        sortDirection === 'desc' ? <ArrowDownIcon className="ml-1 h-3 w-3" /> : <ArrowUpIcon className="ml-1 h-3 w-3" />
      )}
    </Button>
  );

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex gap-4">
            <Skeleton className="h-10 w-64" />
            <Skeleton className="h-10 w-32" />
          </div>
          <div className="space-y-2">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </div>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      <Card className="p-6">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Crypto Valuation Metrics</h2>
              {lastUpdated && (
                <p className="text-sm text-muted-foreground">
                  Last updated: {new Date(lastUpdated).toLocaleTimeString()}
                </p>
              )}
            </div>
            <Badge variant="secondary">{filteredAndSortedData.length} assets</Badge>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search assets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterType} onValueChange={(value: ValuationFilterType) => setFilterType(value)}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Assets</SelectItem>
                <SelectItem value="defi">DeFi Protocols</SelectItem>
                <SelectItem value="has_fees">With Fees Data</SelectItem>
                <SelectItem value="has_revenue">With Revenue Data</SelectItem>
                <SelectItem value="layer1">Layer 1</SelectItem>
                <SelectItem value="layer2">Layer 2</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {DEFI_CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60px]">Rank</TableHead>
                  <TableHead className="min-w-[200px]">Asset</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead className="text-right">
                    <SortButton field="market_cap">Market Cap</SortButton>
                  </TableHead>
                  <TableHead className="text-right">
                    <SortButton field="tvl">TVL</SortButton>
                  </TableHead>
                  <TableHead className="text-right">
                    <Tooltip>
                      <TooltipTrigger>
                        <SortButton field="deltas_tvl_7d">
                          Δ TVL 7d <InfoIcon className="ml-1 h-3 w-3" />
                        </SortButton>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>TVL change over the last 7 days</p>
                      </TooltipContent>
                    </Tooltip>
                  </TableHead>
                  <TableHead className="text-right">
                    <Tooltip>
                      <TooltipTrigger>
                        <span className="flex items-center justify-end font-medium text-sm text-muted-foreground">
                          Δ 30d <InfoIcon className="ml-1 h-3 w-3" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>TVL change over the last 30 days</p>
                      </TooltipContent>
                    </Tooltip>
                  </TableHead>
                  <TableHead className="text-right">
                    <Tooltip>
                      <TooltipTrigger className="flex items-center gap-1">
                        Fees 7d <InfoIcon className="h-3 w-3" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Protocol fees generated in the last 7 days</p>
                      </TooltipContent>
                    </Tooltip>
                  </TableHead>
                  <TableHead className="text-right">
                    <Tooltip>
                      <TooltipTrigger className="flex items-center gap-1">
                        Δ24h <InfoIcon className="h-3 w-3" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Fees change in the last 24 hours</p>
                      </TooltipContent>
                    </Tooltip>
                  </TableHead>
                  <TableHead className="text-right">
                    <Tooltip>
                      <TooltipTrigger className="flex items-center gap-1">
                        Δ30d <InfoIcon className="h-3 w-3" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Fees change in the last 30 days</p>
                      </TooltipContent>
                    </Tooltip>
                  </TableHead>
                  <TableHead className="text-right">
                    <Tooltip>
                      <TooltipTrigger>
                        <SortButton field="p_fees">
                          P/F <InfoIcon className="ml-1 h-3 w-3" />
                        </SortButton>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Market Cap / Annualized Fees</p>
                      </TooltipContent>
                    </Tooltip>
                  </TableHead>
                  <TableHead className="text-right">
                    <Tooltip>
                      <TooltipTrigger>
                        <SortButton field="p_revenue">
                          P/R <InfoIcon className="ml-1 h-3 w-3" />
                        </SortButton>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Market Cap / Annualized Revenue</p>
                      </TooltipContent>
                    </Tooltip>
                  </TableHead>
                  <TableHead className="text-right">24h</TableHead>
                  <TableHead className="text-right">7d</TableHead>
                  <TableHead className="text-center">Category</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSortedData.map((asset) => (
                  <TableRow
                    key={asset.slug}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => onAssetSelect(asset)}
                  >
                    <TableCell className="font-mono text-sm text-muted-foreground">
                      {asset.rank}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {asset.image && (
                          <img src={asset.image} alt={asset.name} className="h-8 w-8 rounded-full" />
                        )}
                        <div>
                          <div className="font-medium">{asset.name}</div>
                          <div className="text-sm text-muted-foreground">{asset.symbol}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatUSD(asset.price, 2)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatUSD(asset.market_cap || asset.mcap_usd)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {asset.data_flags.has_tvl ? (
                        <Tooltip>
                          <TooltipTrigger>
                            <span className="flex items-center gap-1 justify-end">
                              {formatUSD(asset.tvl)}
                              <InfoIcon className="h-3 w-3 text-muted-foreground" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>
                              {asset.data_flags.tvl_source === 'chain'
                                ? `Chain TVL: ${asset.data_flags.tvl_source_label} (DeFiLlama)`
                                : `Protocol TVL: ${asset.data_flags.tvl_source_label} (DeFiLlama)`
                              }
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Badge variant="secondary" className="text-xs">N/A</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {asset.data_flags.has_tvl && asset.deltas?.tvl_7d != null ? (
                        <span className={cn(
                          "inline-flex items-center gap-1 text-sm font-medium",
                          asset.deltas.tvl_7d >= 0 ? "text-success" : "text-destructive"
                        )}>
                          {asset.deltas.tvl_7d >= 0 ? <TrendingUpIcon className="h-3 w-3" /> : <TrendingDownIcon className="h-3 w-3" />}
                          {formatPercent(asset.deltas.tvl_7d)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {asset.data_flags.has_tvl ? (
                        asset.deltas?.tvl_30d != null && asset.deltas.tvl_30d !== 0 ? (
                          <span className={cn(
                            "inline-flex items-center gap-1 text-sm font-medium",
                            asset.deltas.tvl_30d >= 0 ? "text-success" : "text-destructive"
                          )}>
                            {asset.deltas.tvl_30d >= 0 ? <TrendingUpIcon className="h-3 w-3" /> : <TrendingDownIcon className="h-3 w-3" />}
                            {formatPercent(asset.deltas.tvl_30d)}
                          </span>
                        ) : (
                          asset.tvl_source_key && asset.tvl_source_key !== asset.slug ? (
                            <Tooltip>
                              <TooltipTrigger>
                                <Badge variant="outline" className="text-xs text-muted-foreground border-dashed">
                                  Inherited
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Δ30d herdado de {asset.tvl_source_key}</p>
                              </TooltipContent>
                            </Tooltip>
                          ) : <span className="text-muted-foreground">—</span>
                        )
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {asset.data_flags.has_fees ? formatUSD(asset.fees_7d) : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {asset.fees_change_1d != null ? (
                        <Badge
                          variant="outline"
                          className={cn(
                            "text-xs font-medium",
                            asset.fees_change_1d >= 0
                              ? "bg-profit/10 text-profit border-profit/30"
                              : "bg-loss/10 text-loss border-loss/30"
                          )}
                        >
                          {asset.fees_change_1d >= 0 ? '↑' : '↓'} {formatPercent(Math.abs(asset.fees_change_1d))}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {asset.fees_change_30d != null ? (
                        <Badge
                          variant="outline"
                          className={cn(
                            "text-xs font-medium",
                            asset.fees_change_30d >= 0
                              ? "bg-profit/10 text-profit border-profit/30"
                              : "bg-loss/10 text-loss border-loss/30"
                          )}
                        >
                          {asset.fees_change_30d >= 0 ? '↑' : '↓'} {formatPercent(Math.abs(asset.fees_change_30d))}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {asset.p_fees !== null ? formatRatio(asset.p_fees) : '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {asset.p_revenue !== null ? formatRatio(asset.p_revenue) : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={cn(
                        "inline-flex items-center gap-1 text-sm font-medium",
                        (asset.change_24h || 0) >= 0 ? "text-success" : "text-destructive"
                      )}>
                        {(asset.change_24h || 0) >= 0 ? <TrendingUpIcon className="h-3 w-3" /> : <TrendingDownIcon className="h-3 w-3" />}
                        {formatPercent(asset.change_24h)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={cn(
                        "inline-flex items-center gap-1 text-sm font-medium",
                        (asset.change_7d || 0) >= 0 ? "text-success" : "text-destructive"
                      )}>
                        {(asset.change_7d || 0) >= 0 ? <TrendingUpIcon className="h-3 w-3" /> : <TrendingDownIcon className="h-3 w-3" />}
                        {formatPercent(asset.change_7d)}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <CategoryBadge category={asset.defi_category || asset.category} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </Card>
    </TooltipProvider>
  );
}
